import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
	siteGetSites,
	dataExportGetSiteHierarchy,
	dataExportGetAnnualExport,
} from '../../../api';
import type { SmartField_Api_Features_DataWarehouse_DataExport_ObservationKind } from '../../../api';
import { ExportSiteMap } from './ExportSiteMap';
import { ExportFilterPanel } from './ExportFilterPanel';
import { ExportHierarchyPanel } from './ExportHierarchyPanel';
import { ExportPreviewPanel } from './ExportPreviewPanel';
import { ExportJobsBar, type TrackedJob } from './ExportJobsBar';
import { ChamberFluxReadmeModal } from './ChamberFluxReadmeModal';
import { DataTabs } from '../DataTabs';
import { toLocalStartIso, toLocalEndIso, isValidDateStr } from '../../../utils/dateUtils';
import { useAuthStore, useIsRawDataReaderForSite, useIsFluxDataReaderForSite, isRawDataReaderForSite, isFluxDataReaderForSite } from '../../../lib/auth';

type AnnualDownloadState =
	| { status: 'idle' }
	| { status: 'loading' }
	| { status: 'ready'; url: string; chamberFluxUrl: string | null; towerFluxUrls: Record<string, string>; generatedAt: string }
	| { status: 'generating' }
	| { status: 'error'; message: string };

const currentAnnualYear = new Date().getFullYear();

// Tower flux downloads are disabled until the results are quality assured.
const towerFluxDownloadsEnabled = false;

function AnnualDownloadSection({
	siteCode,
}: {
	siteCode: string;
}) {
	const [year, setYear] = useState(currentAnnualYear);
	const [state, setState] = useState<AnnualDownloadState>({ status: 'idle' });

	const handleYearChange = (newYear: number) => {
		setYear(newYear);
		setState({ status: 'idle' });
	};

	const pollForLink = useCallback(async () => {
		setState({ status: 'loading' });
		try {
			const body = await dataExportGetAnnualExport({ siteCode, year }) as { url: string; chamber_flux_url?: string | null; gradient_flux_urls?: Record<string, string>; generatedAt: string } | { status: string; retryAfterSeconds: number } | null;
			if (body && 'url' in body) {
				setState({ status: 'ready', url: body.url, chamberFluxUrl: body.chamber_flux_url ?? null, towerFluxUrls: body.gradient_flux_urls ?? {}, generatedAt: body.generatedAt });
			} else {
				setState({ status: 'generating' });
			}
		} catch (err: unknown) {
			const status = (err as { status?: number })?.status;
			const detail = (err as { body?: { detail?: string; title?: string } })?.body?.detail
				?? (err as { body?: { title?: string } })?.body?.title;
			if (status === 202) {
				setState({ status: 'generating' });
			} else {
				setState({ status: 'error', message: detail ?? (status ? `Server returned ${status}` : 'Network error') });
			}
		}
	}, [siteCode, year]);

	useEffect(() => {
		setState({ status: 'idle' });
	}, [siteCode, year]);

	return (
		<div className="bg-white rounded-lg shadow p-4">
			<div className="flex items-center justify-between mb-3">
				<h3 className="font-semibold text-gray-900 text-sm">Annual dataset</h3>
				<div className="flex items-center gap-1.5">
					<button
						onClick={() => handleYearChange(year - 1)}
						className="px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm"
					>‹</button>
					<span className="text-sm font-semibold text-gray-900 w-12 text-center">{year}</span>
					<button
						onClick={() => handleYearChange(year + 1)}
						disabled={year >= currentAnnualYear}
						className="px-2 py-0.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm disabled:opacity-40"
					>›</button>
				</div>
			</div>
			<p className="text-xs text-gray-500 mb-4">
				Full-site export (all fields, treatments, plots, sensor data).
				Includes a JSON file and a wide-format chamber flux CSV.
				Links are valid for 7 days.
			</p>
			<p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-4">
				Gradient (tower) flux download disabled temporarily — not quality assured yet.
			</p>

			{state.status === 'idle' && (
				<button
					onClick={pollForLink}
					className="w-full py-2 px-4 bg-smartfield text-white rounded-lg hover:bg-smartfield/90 transition-colors font-medium text-sm"
				>
					Get download link
				</button>
			)}

			{state.status === 'loading' && (
				<button disabled className="w-full py-2 px-4 bg-smartfield/50 text-white rounded-lg font-medium text-sm cursor-not-allowed flex items-center justify-center gap-2">
					<svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
						<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
						<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
					</svg>
					Checking…
				</button>
			)}

			{state.status === 'ready' && (
				<div className="space-y-2">
					<button
						onClick={async () => {
							let objectUrl: string | null = null;
							try {
								const res = await fetch(state.url);
								if (!res.ok) {
									setState({ status: 'error', message: `Download failed: server returned ${res.status}` });
									return;
								}
								const blob = await res.blob();
								objectUrl = URL.createObjectURL(blob);
								const a = document.createElement('a');
								a.href = objectUrl;
								a.download = `${siteCode}_${year}.json`;
								a.click();
							} catch {
								setState({ status: 'error', message: 'Download failed: network error' });
							} finally {
								if (objectUrl) {
									setTimeout(() => URL.revokeObjectURL(objectUrl!), 60_000);
								}
							}
						}}
						className="block w-full py-2 px-4 bg-smartfield text-white rounded-lg hover:bg-smartfield/90 transition-colors font-medium text-sm text-center"
					>
						Download {year} dataset (.json)
					</button>
					{state.chamberFluxUrl && (
						<a
							href={state.chamberFluxUrl}
							download={`${siteCode}_${year}_chamber_flux.csv`}
							className="block w-full py-2 px-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm text-center"
						>
							Download {year} chamber flux (.csv)
						</a>
					)}
					{towerFluxDownloadsEnabled && Object.entries(state.towerFluxUrls).map(([towerName, towerUrl]) => (
						<a
							key={towerName}
							href={towerUrl}
							download={`${siteCode}_${year}_gradient_flux_${towerName}.csv`}
							className="block w-full py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium text-sm text-center"
						>
							Download {year} tower flux — {towerName} (.csv)
						</a>
					))}
					<p className="text-xs text-gray-400 text-center">
						Generated {new Date(state.generatedAt).toLocaleString()}
					</p>
				</div>
			)}

			{state.status === 'generating' && (
				<div className="space-y-2">
					<div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
						<svg className="animate-spin h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24">
							<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
							<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
						</svg>
						Generating dataset — check back in ~5 minutes.
					</div>
					<button
						onClick={pollForLink}
						className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
					>
						Check again
					</button>
				</div>
			)}

			{state.status === 'error' && (
				<div className="space-y-2">
					<p className="text-xs text-red-600">{state.message}</p>
					<button
						onClick={pollForLink}
						className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
					>
						Retry
					</button>
				</div>
			)}
		</div>
	);
}

const currentYear = new Date().getFullYear();

export function ExportPage() {
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedSiteSerialNumber, setSelectedSiteSerialNumber] = useState<string | null>(null);
	const [selectedLocationIds, setSelectedLocationIds] = useState<Set<number>>(new Set());
	const [selectedSensorIds, setSelectedSensorIds] = useState<Set<number>>(new Set());
	const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
	const [endDate, setEndDate] = useState(`${currentYear}-12-31`);
	const [mapAsOf, setMapAsOf] = useState(`${currentYear}-12-31`);
	const [mapAsOfUserChanged, setMapAsOfUserChanged] = useState(false);
	const [dataTypes, setDataTypes] = useState<Set<SmartField_Api_Features_DataWarehouse_DataExport_ObservationKind>>(new Set(['Manual', 'ChamberFlux', 'Sensor']));
	const [excludedMeasureGroups, setExcludedMeasureGroups] = useState<Set<string>>(new Set());
	const [trackedJobs, setTrackedJobs] = useState<TrackedJob[]>([]);

	// Chamber-by-number mode (only for ChamberFlux exports)
	const [chamberMode, setChamberMode] = useState(false);
	const [selectedChannels, setSelectedChannels] = useState<number[]>([]);
	const [includeSiteSensors, setIncludeSiteSensors] = useState(false);

	// Hierarchy open-state lifted here so map clicks can auto-expand paths
	const [openFields, setOpenFields] = useState<Set<number>>(new Set());
	const [openTreatments, setOpenTreatments] = useState<Set<string>>(new Set());
	const [showReadme, setShowReadme] = useState(false);

	const { data: sites = [], isLoading: sitesLoading } = useQuery({
		queryKey: ['sites'],
		queryFn: () => siteGetSites(),
	});

	const { user } = useAuthStore();
	const selectedSiteId = useMemo(() => {
		return sites.find(s => s.serialNumber === selectedSiteSerialNumber)?.id ?? -1;
	}, [sites, selectedSiteSerialNumber]);
	const canReadRaw = useIsRawDataReaderForSite(selectedSiteId);
	const canReadFlux = useIsFluxDataReaderForSite(selectedSiteId);

	const [debouncedStartDate, setDebouncedStartDate] = useState(startDate);
	const [debouncedEndDate, setDebouncedEndDate] = useState(endDate);
	const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (debounceTimer.current) { clearTimeout(debounceTimer.current); }
		debounceTimer.current = setTimeout(() => {
			setDebouncedStartDate(startDate);
			setDebouncedEndDate(endDate);
		}, 400);
		return () => { if (debounceTimer.current) { clearTimeout(debounceTimer.current); } };
	}, [startDate, endDate]);

	// Auto-sync mapAsOf to endDate unless user has manually changed it
	useEffect(() => {
		if (!mapAsOfUserChanged && isValidDateStr(endDate)) {
			setMapAsOf(endDate);
		}
	}, [endDate, mapAsOfUserChanged]);

	const handleMapAsOfChange = (date: string) => {
		setMapAsOf(date);
		setMapAsOfUserChanged(true);
	};

	const handleMapAsOfReset = () => {
		setMapAsOf(endDate);
		setMapAsOfUserChanged(false);
	};

	const datesValid = isValidDateStr(debouncedStartDate) && isValidDateStr(debouncedEndDate) && debouncedStartDate <= debouncedEndDate;
	const hierarchyStartIso = selectedSiteSerialNumber && datesValid ? toLocalStartIso(debouncedStartDate) : '';
	const hierarchyEndIso = selectedSiteSerialNumber && datesValid ? toLocalEndIso(debouncedEndDate) : '';

	const { data: hierarchy, isLoading: hierarchyLoading } = useQuery({
		queryKey: ['hierarchy', selectedSiteSerialNumber, debouncedStartDate, debouncedEndDate],
		queryFn: () => dataExportGetSiteHierarchy({
			siteSerialNumber: selectedSiteSerialNumber!,
			startTime: hierarchyStartIso,
			endTime: hierarchyEndIso,
		}),
		enabled: selectedSiteSerialNumber !== null && datesValid,
	});

	const mapAsOfValid = isValidDateStr(mapAsOf);
	const { data: mapHierarchy } = useQuery({
		queryKey: ['hierarchy-map', selectedSiteSerialNumber, mapAsOf],
		queryFn: () => dataExportGetSiteHierarchy({
			siteSerialNumber: selectedSiteSerialNumber!,
			startTime: toLocalStartIso(mapAsOf),
			endTime: toLocalEndIso(mapAsOf),
		}),
		enabled: selectedSiteSerialNumber !== null && mapAsOfValid,
	});

	// Stable treatment key: fieldId + desc + sorted plot locationIds (prevents collisions on duplicate/empty desc).
	const makeTreatmentKey = (fieldId: number, desc: string, plots: { locationId?: number | null }[]) => {
		const plotIds = plots.map(p => p.locationId ?? 0).sort((a, b) => a - b).join(',');
		return `${fieldId}|${desc}|${plotIds}`;
	};

	// Build lookup: locationId → { fieldId, treatmentKey (composite), treatmentDescription }
	const plotPathMap = useMemo(() => {
		const map = new Map<number, { fieldId: number; treatmentKey: string; treatmentDescription: string }>();
		for (const field of hierarchy?.fields ?? []) {
			for (const treatment of field.treatments ?? []) {
				const desc = treatment.treatmentDescription ?? '';
				const key = makeTreatmentKey(field.fieldId!, desc, treatment.plots ?? []);
				for (const plot of treatment.plots ?? []) {
					if (field.fieldId != null && plot.locationId != null) {
						map.set(plot.locationId, {
							fieldId: field.fieldId,
							treatmentKey: key,
							treatmentDescription: desc,
						});
					}
				}
			}
		}
		return map;
	}, [hierarchy]);

	// Build lookup: treatmentKey → locationIds
	const treatmentPlotMap = useMemo(() => {
		const map = new Map<string, number[]>();
		for (const field of hierarchy?.fields ?? []) {
			for (const treatment of field.treatments ?? []) {
				const key = makeTreatmentKey(field.fieldId!, treatment.treatmentDescription ?? '', treatment.plots ?? []);
				map.set(key, (treatment.plots ?? []).filter(p => p.locationId != null).map(p => p.locationId!));
			}
		}
		return map;
	}, [hierarchy]);

	// Build lookup: treatmentKey → sensorIds
	const treatmentSensorMap = useMemo(() => {
		const map = new Map<string, number[]>();
		for (const field of hierarchy?.fields ?? []) {
			for (const treatment of field.treatments ?? []) {
				const key = makeTreatmentKey(field.fieldId!, treatment.treatmentDescription ?? '', treatment.plots ?? []);
				const sensorIds: number[] = [];
				for (const plot of treatment.plots ?? []) {
					for (const sensor of plot.sensors ?? []) {
						if (sensor.sensorId != null) {
							sensorIds.push(sensor.sensorId);
						}
					}
				}
				map.set(key, sensorIds);
			}
		}
		return map;
	}, [hierarchy]);

	// Build lookup: fieldId → locationIds
	const fieldPlotMap = useMemo(() => {
		const map = new Map<number, number[]>();
		for (const field of hierarchy?.fields ?? []) {
			if (field.fieldId == null) { continue; }
			const ids: number[] = [];
			for (const treatment of field.treatments ?? []) {
				for (const plot of treatment.plots ?? []) {
					if (plot.locationId != null) { ids.push(plot.locationId); }
				}
			}
			map.set(field.fieldId, ids);
		}
		return map;
	}, [hierarchy]);

	// Build lookup: locationId → hasGradientFlux (tower link)
	const plotGradientFluxMap = useMemo(() => {
		const map = new Map<number, boolean>();
		for (const field of hierarchy?.fields ?? []) {
			for (const treatment of field.treatments ?? []) {
				for (const plot of treatment.plots ?? []) {
					if (plot.locationId != null) {
						map.set(plot.locationId, !!plot.hasGradientFlux);
					}
				}
			}
		}
		return map;
	}, [hierarchy]);

	// Build lookup: locationId → sensorIds
	const plotSensorMap = useMemo(() => {
		const map = new Map<number, number[]>();
		for (const field of hierarchy?.fields ?? []) {
			for (const treatment of field.treatments ?? []) {
				for (const plot of treatment.plots ?? []) {
					if (plot.locationId == null) { continue; }
					const ids: number[] = [];
					for (const sensor of plot.sensors ?? []) {
						if (sensor.sensorId != null) { ids.push(sensor.sensorId); }
					}
					map.set(plot.locationId, ids);
				}
			}
		}
		return map;
	}, [hierarchy]);

	// Build lookup: fieldId → sensorIds
	const fieldSensorMap = useMemo(() => {
		const map = new Map<number, number[]>();
		for (const field of hierarchy?.fields ?? []) {
			if (field.fieldId == null) { continue; }
			const ids: number[] = [];
			for (const treatment of field.treatments ?? []) {
				for (const plot of treatment.plots ?? []) {
					for (const sensor of plot.sensors ?? []) {
						if (sensor.sensorId != null) { ids.push(sensor.sensorId); }
					}
				}
			}
			map.set(field.fieldId, ids);
		}
		return map;
	}, [hierarchy]);

	// Derived: which fields/treatments are (partially/fully) selected
	const selectedFields = useMemo(() => {
		const set = new Set<number>();
		for (const plotId of selectedLocationIds) {
			const path = plotPathMap.get(plotId);
			if (path) {
				set.add(path.fieldId);
			}
		}
		return set;
	}, [selectedLocationIds, plotPathMap]);


	const selectedHasGradientFlux = useMemo(() => {
		for (const plotId of selectedLocationIds) {
			if (plotGradientFluxMap.get(plotId)) {
				return true;
			}
		}
		return false;
	}, [selectedLocationIds, plotGradientFluxMap]);

	// Auto-expand first field when hierarchy loads for a newly selected site
	useEffect(() => {
		const firstFieldId = hierarchy?.fields?.[0]?.fieldId;
		if (firstFieldId != null) {
			setOpenFields(prev => {
				const next = new Set(prev);
				next.add(firstFieldId);
				return next;
			});
		}
	}, [hierarchy]);

	const stripDisallowedKinds = useCallback((serialNumber: string | null) => {
		const site = sites.find(s => s.serialNumber === serialNumber);
		if (!site?.id) {
			return;
		}
		const canRaw = isRawDataReaderForSite(user, site.id);
		const canFlux = isFluxDataReaderForSite(user, site.id);
		setDataTypes(prev => {
			const next = new Set(prev);
			if (!canRaw) {
				next.delete('Manual');
				next.delete('Sensor');
			}
			if (!canFlux) {
				next.delete('ChamberFlux');
			}
			return next;
		});
	}, [sites, user]);

	// Site came from ?site= query param — validate against loaded sites, then clear the param either way.
	useEffect(() => {
		const hasSiteParam = searchParams.has('site');
		const paramSite = searchParams.get('site');
		if (!hasSiteParam || sitesLoading) {
			return;
		}
		if (paramSite && sites.some(s => s.serialNumber === paramSite)) {
			setSelectedSiteSerialNumber(paramSite);
			stripDisallowedKinds(paramSite);
		} else {
			setSelectedSiteSerialNumber(null);
		}
		setSearchParams(params => {
			params.delete('site');
			return params;
		}, { replace: true });
	}, [sites, sitesLoading, searchParams, setSearchParams, setSelectedSiteSerialNumber, stripDisallowedKinds]);

	const handleSiteSelect = (serialNumber: string) => {
		const next = serialNumber === selectedSiteSerialNumber ? null : serialNumber;
		setSelectedSiteSerialNumber(next);
		setSelectedLocationIds(new Set());
		setSelectedSensorIds(new Set());
		setOpenFields(new Set());
		setOpenTreatments(new Set());
		stripDisallowedKinds(next);
	};

	const handleSiteFilterChange = (serialNumber: string | null) => {
		setSelectedSiteSerialNumber(serialNumber);
		setSelectedLocationIds(new Set());
		setSelectedSensorIds(new Set());
		setOpenFields(new Set());
		setOpenTreatments(new Set());
		stripDisallowedKinds(serialNumber);
	};

	const handleFieldSelect = useCallback((fieldId: number) => {
		const plotIds = fieldPlotMap.get(fieldId) ?? [];
		const sensorIds = fieldSensorMap.get(fieldId) ?? [];
		let deselect = false;
		setSelectedLocationIds(prev => {
			deselect = plotIds.some(id => prev.has(id));
			const next = new Set(prev);
			if (deselect) {
				for (const id of plotIds) { next.delete(id); }
			} else {
				for (const id of plotIds) { next.add(id); }
			}
			return next;
		});
		setSelectedSensorIds(prev => {
			const shouldDeselect = deselect;
			const next = new Set(prev);
			if (shouldDeselect) {
				for (const id of sensorIds) { next.delete(id); }
			} else {
				for (const id of sensorIds) { next.add(id); }
			}
			return next;
		});
		setOpenFields(prev => { const next = new Set(prev); next.add(fieldId); return next; });
	}, [fieldPlotMap, fieldSensorMap]);

	const handleTreatmentToggle = useCallback((treatmentKey: string, fieldId: number) => {
		const plotIds = treatmentPlotMap.get(treatmentKey) ?? [];
		const sensorIds = treatmentSensorMap.get(treatmentKey) ?? [];

		const deselect = plotIds.some(id => selectedLocationIds.has(id)) || sensorIds.some(id => selectedSensorIds.has(id));

		setSelectedLocationIds(prev => {
			const next = new Set(prev);
			if (deselect) {
				for (const id of plotIds) { next.delete(id); }
			} else {
				for (const id of plotIds) { next.add(id); }
			}
			return next;
		});

		setSelectedSensorIds(prev => {
			const next = new Set(prev);
			if (deselect) {
				for (const id of sensorIds) { next.delete(id); }
			} else {
				for (const id of sensorIds) { next.add(id); }
			}
			return next;
		});

		setOpenFields(prev => { const next = new Set(prev); next.add(fieldId); return next; });
		setOpenTreatments(prev => { const next = new Set(prev); next.add(treatmentKey); return next; });
	}, [treatmentPlotMap, treatmentSensorMap, selectedLocationIds, selectedSensorIds]);

	const handlePlotToggle = useCallback((locationId: number) => {
		const sensorIds = plotSensorMap.get(locationId) ?? [];
		let deselecting = false;
		setSelectedLocationIds(prev => {
			deselecting = prev.has(locationId);
			const next = new Set(prev);
			if (deselecting) {
				next.delete(locationId);
			} else {
				next.add(locationId);
			}
			return next;
		});
		setSelectedSensorIds(prev => {
			const next = new Set(prev);
			if (deselecting) {
				for (const id of sensorIds) { next.delete(id); }
			} else {
				for (const id of sensorIds) { next.add(id); }
			}
			return next;
		});
		const path = plotPathMap.get(locationId);
		if (path) {
			setOpenFields(prev => {
				const next = new Set(prev);
				next.add(path.fieldId);
				return next;
			});
			setOpenTreatments(prev => {
				const next = new Set(prev);
				next.add(path.treatmentKey);
				return next;
			});
		}
	}, [plotPathMap, plotSensorMap]);

	const handleClearSelection = () => {
		setSelectedLocationIds(new Set());
		setSelectedSensorIds(new Set());
	};

	const allPlotIds = useMemo(() => {
		const ids: number[] = [];
		for (const field of hierarchy?.fields ?? []) {
			for (const treatment of field.treatments ?? []) {
				for (const plot of treatment.plots ?? []) {
					if (plot.locationId != null) { ids.push(plot.locationId); }
				}
			}
		}
		return ids;
	}, [hierarchy]);

	const allSensorIds = useMemo(() => {
		const ids: number[] = [];
		for (const field of hierarchy?.fields ?? []) {
			for (const treatment of field.treatments ?? []) {
				for (const plot of treatment.plots ?? []) {
					for (const sensor of plot.sensors ?? []) {
						if (sensor.sensorId != null) { ids.push(sensor.sensorId); }
					}
				}
			}
		}
		return ids;
	}, [hierarchy]);

	const handleSiteToggle = useCallback(() => {
		let allSelected = false;
		setSelectedLocationIds(prev => {
			allSelected = allPlotIds.length > 0 && allPlotIds.every(id => prev.has(id));
			return allSelected ? new Set() : new Set(allPlotIds);
		});
		setSelectedSensorIds(() => allSelected ? new Set() : new Set(allSensorIds));
	}, [allPlotIds, allSensorIds]);

	const siteSensorIds = useMemo(() => {
		return (hierarchy?.siteSensors ?? []).map(s => s.sensorId).filter((id): id is number => id != null);
	}, [hierarchy]);

	useEffect(() => {
		if (!includeSiteSensors) {
			return;
		}
		setSelectedSensorIds(prev => {
			const next = new Set(prev);
			for (const id of siteSensorIds) { next.add(id); }
			return next;
		});
	}, [siteSensorIds, includeSiteSensors]);

	const handleSiteSensorsToggle = useCallback((include: boolean) => {
		setIncludeSiteSensors(include);
		setSelectedSensorIds(prev => {
			const next = new Set(prev);
			if (include) {
				for (const id of siteSensorIds) { next.add(id); }
			} else {
				for (const id of siteSensorIds) { next.delete(id); }
			}
			return next;
		});
	}, [siteSensorIds]);

	const manualMeasureGroups = hierarchy?.manualMeasureGroups ?? [];
	const selectedMeasureGroups = excludedMeasureGroups.size > 0
		? manualMeasureGroups.filter(g => !excludedMeasureGroups.has(g))
		: undefined;

	const handleMeasureGroupToggle = (group: string, checked: boolean) => {
		setExcludedMeasureGroups(prev => {
			const next = new Set(prev);
			if (checked) {
				next.delete(group);
			} else {
				next.add(group);
			}
			return next;
		});
	};

	const handleDataTypeChange = (type: SmartField_Api_Features_DataWarehouse_DataExport_ObservationKind, checked: boolean) => {
		if (checked && (type === 'ChamberFlux' ? !canReadFlux : !canReadRaw)) {
			return;
		}
		setDataTypes(prev => {
			const next = new Set(prev);
			if (checked) {
				next.add(type);
			} else {
				next.delete(type);
			}
			return next;
		});
		if (type === 'ChamberFlux' && !checked) {
			setChamberMode(false);
		}
	};

	const handleToggleField = (fieldId: number) => {
		setOpenFields(prev => {
			const next = new Set(prev);
			if (next.has(fieldId)) {
				next.delete(fieldId);
			} else {
				next.add(fieldId);
			}
			return next;
		});
	};

	const handleToggleTreatment = (treatmentKey: string) => {
		setOpenTreatments(prev => {
			const next = new Set(prev);
			if (next.has(treatmentKey)) {
				next.delete(treatmentKey);
			} else {
				next.add(treatmentKey);
			}
			return next;
		});
	};

	const handleJobQueued = useCallback((job: TrackedJob) => {
		setTrackedJobs(prev => [job, ...prev]);
	}, []);

	const handleJobUpdate = useCallback((jobId: string, update: Partial<TrackedJob>) => {
		setTrackedJobs(prev => prev.map(j => j.jobId === jobId ? { ...j, ...update } : j));
	}, []);

	const handleJobDismiss = useCallback((jobId: string) => {
		setTrackedJobs(prev => prev.filter(j => j.jobId !== jobId));
	}, []);

	const jobsVisible = trackedJobs.length > 0;

	return (
		<div className={`space-y-6 ${jobsVisible ? 'pb-24' : ''}`}>
			{hierarchyLoading && (
				<div className="fixed inset-0 bg-white/60 backdrop-blur-sm z-[1100] flex items-center justify-center pointer-events-none">
					<div className="flex flex-col items-center gap-3 bg-white rounded-xl shadow-lg px-8 py-6">
						<svg className="animate-spin h-8 w-8 text-smartfield" fill="none" viewBox="0 0 24 24">
							<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
							<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
						</svg>
						<p className="text-sm text-gray-700 font-medium">Loading site data…</p>
					</div>
				</div>
			)}
			<div className="flex items-center justify-between">
				<h1 className="text-3xl font-bold text-gray-900">Data Export</h1>
				<button
					onClick={() => setShowReadme(true)}
					className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-smartfield transition-colors"
				>
					<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
						<path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
					</svg>
					About this export
				</button>
			</div>

			<DataTabs />

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				<div className="lg:col-span-2">
					{sitesLoading ? (
						<div className="h-96 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center text-gray-400 text-sm">
							Loading sites…
						</div>
					) : (
						<div className="relative">
							<ExportSiteMap
								sites={sites}
								selectedSiteSerialNumber={selectedSiteSerialNumber}
								selectedLocationIds={selectedLocationIds}
								hierarchy={mapHierarchy ?? hierarchy}
								onSiteSelect={handleSiteSelect}
								onPlotSelect={handlePlotToggle}
							/>
							{selectedSiteSerialNumber && (
								<div className="absolute top-3 right-3 z-[1000] flex items-center gap-1.5 bg-white/90 backdrop-blur-sm rounded-lg shadow px-2.5 py-1.5 border border-gray-200">
									<label className="text-xs text-gray-600 font-medium whitespace-nowrap">Map as of</label>
									<input
										type="date"
										value={mapAsOf}
										onChange={e => handleMapAsOfChange(e.target.value)}
										className="px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-smartfield focus:border-smartfield bg-white text-gray-900"
									/>
									{mapAsOfUserChanged && (
										<button
											type="button"
											onClick={handleMapAsOfReset}
											className="text-xs text-amber-700 underline whitespace-nowrap"
										>
											Reset
										</button>
									)}
								</div>
							)}
						</div>
					)}
				</div>
				<div>
					<ExportFilterPanel
						sites={sites}
						selectedSiteSerialNumber={selectedSiteSerialNumber}
						onSiteChange={handleSiteFilterChange}
						startDate={startDate}
						endDate={endDate}
						onStartDateChange={setStartDate}
						onEndDateChange={setEndDate}
						dataTypes={dataTypes}
						onDataTypeChange={handleDataTypeChange}
						measureGroups={manualMeasureGroups}
						excludedMeasureGroups={excludedMeasureGroups}
						onMeasureGroupToggle={handleMeasureGroupToggle}
						canReadRaw={canReadRaw}
						canReadFlux={canReadFlux}
					/>
				</div>
			</div>

			{!selectedSiteSerialNumber ? (
				<div className="bg-gray-50 border border-gray-200 rounded-lg px-6 py-10 text-center">
					<svg className="mx-auto h-10 w-10 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
					</svg>
					<p className="text-sm font-medium text-gray-600 mb-1">Select a site to start</p>
					<p className="text-xs text-gray-400">Click a site on the map or choose one from the filter panel above.</p>
				</div>
			) : (
				<>
					<ExportHierarchyPanel
						hierarchy={hierarchy}
						selectedFields={selectedFields}
						selectedLocationIds={selectedLocationIds}
						selectedSensorIds={selectedSensorIds}
						openFields={openFields}
						openTreatments={openTreatments}
						dataTypes={dataTypes}
						onFieldToggle={handleToggleField}
						onFieldSelect={handleFieldSelect}
						onTreatmentToggle={handleTreatmentToggle}
						onPlotToggle={handlePlotToggle}
						onTreatmentExpandToggle={handleToggleTreatment}
						onClearSelection={handleClearSelection}
						onSelectAll={handleSiteToggle}
						onSiteToggle={handleSiteToggle}
						totalPlotCount={allPlotIds.length}
						treatmentPlotMap={treatmentPlotMap}
						fieldPlotMap={fieldPlotMap}
						chamberMode={chamberMode}
						onChamberModeChange={dataTypes.has('ChamberFlux') ? setChamberMode : undefined}
						includeSiteSensors={includeSiteSensors}
						onSiteSensorsToggle={handleSiteSensorsToggle}
					/>

					<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
						<div className="lg:col-span-2">
							<ExportPreviewPanel
								startDate={startDate}
								endDate={endDate}
								siteSerialNumber={selectedSiteSerialNumber}
								siteDisplayName={hierarchy?.displayName ?? null}
								selectedLocationIds={selectedLocationIds}
								selectedSensorIds={selectedSensorIds}
								dataTypes={dataTypes}
								measureGroups={selectedMeasureGroups}
								selectedHasGradientFlux={selectedHasGradientFlux}
								onJobQueued={handleJobQueued}
								chamberMode={chamberMode}
								selectedChannels={selectedChannels}
								onChannelsChange={setSelectedChannels}
							/>
						</div>
						<div>
							<AnnualDownloadSection
								siteCode={selectedSiteSerialNumber}
							/>
						</div>
					</div>
				</>
			)}

			<ChamberFluxReadmeModal open={showReadme} onClose={() => setShowReadme(false)} />

			<ExportJobsBar
				jobs={trackedJobs}
				onUpdate={handleJobUpdate}
				onDismiss={handleJobDismiss}
			/>
		</div>
	);
}
